import {
  Box,
  Card,
  Flex,
  Icon,
  Text,
  Input,
  Button,
  Select,
  Heading,
  useTheme,
  FormLabel,
  InputGroup,
  FormControl,
  CurrencyInput,
  FormHelperText,
  InputRightElement
} from '@homebotapp/hb-react-component-catalog'
import { useRef, useState } from 'react'
import { useIntl, defineMessages } from 'react-intl'
import { formatCurrency, formatNumber } from '../../../../../../helpers/formatters'
import { CREDIT_SCORE_VALUE_TO_RANGES } from '../../Utilities/EstimatedMonthlyPaymentConstants'
import { BucketedMonthlyHomeExpenses, ListingLoanInfo } from '../../../../../../api/gqlaxy/gql/generated/graphql'
import { HOME_SEARCH_LISTING_DETAILS_ESTIMATED_MONTHLY_PAYMENT } from '../../../../../../constants/tracking/trackingEventLocations/homeSearchTrackingEventLocation'

export const MSG = defineMessages({
  principalAndInterest: {
    id: 'EstimatedMonthlyPaymentBreakdown.principalAndInterest',
    defaultMessage: 'Principal & interest'
  },
  principalAndInterestTermAndRate: {
    id: 'EstimatedMonthlyPaymentBreakdown.principalAndInterestTermAndRate',
    defaultMessage: '{years} years at {rate}% APR'
  },
  down: {
    id: 'EstimatedMonthlyPaymentBreakdown.down',
    defaultMessage: '{down} down payment'
  },
  downPayment: {
    id: 'EstimatedMonthlyPaymentBreakdown.downPayment',
    defaultMessage: 'Down payment'
  },
  percentage: {
    id: 'EstimatedMonthlyPaymentBreakdown.percentage',
    defaultMessage: 'Percentage'
  },
  max: {
    id: 'EstimatedMonthlyPaymentBreakdown.max',
    defaultMessage: 'max'
  },
  assumedMortgage: {
    id: 'EstimatedMonthlyPaymentBreakdown.assumedMortgage',
    defaultMessage: 'Assumed mortgage'
  },
  assumedMortgageTermAndRate: {
    id: 'EstimatedMonthlyPaymentBreakdown.assumedMortgageTermAndRate',
    defaultMessage: '{years} years remaining {rate}%'
  },
  secondMortgage: {
    id: 'EstimatedMonthlyPaymentBreakdown.secondMortgage',
    defaultMessage: 'Second mortgage'
  },
  assumableExplainerNoSecondMortgage: {
    id: 'EstimatedMonthlyPaymentBreakdown.assumableExplainerNoSecondMortgage',
    defaultMessage:
      'Based on a {down}, you can cover the difference between the assumed mortgage and the listing price.'
  },
  assumableExplainerWithSecondMortgage: {
    id: 'EstimatedMonthlyPaymentBreakdown.assumableExplainerWithSecondMortgage',
    defaultMessage: 'Based on a {down}, you’ll need a second mortgage to cover the listing price.'
  },
  downPaymentInline: {
    id: 'EstimatedMonthlyPaymentBreakdown.downPaymentInline',
    defaultMessage: '{down} down payment'
  },
  secondMortgageTermAndRate: {
    id: 'EstimatedMonthlyPaymentBreakdown.secondMortgageTermAndRate',
    defaultMessage: '{years} years at {rate}%'
  },
  or: {
    id: 'EstimatedMonthlyPaymentBreakdown.or',
    defaultMessage: 'or'
  },
  loanProgram: {
    id: 'EstimatedMonthlyPaymentBreakdown.loanProgram',
    defaultMessage: 'Loan program'
  },
  creditScore: {
    id: 'EstimatedMonthlyPaymentBreakdown.creditScore',
    defaultMessage: 'Credit score'
  },
  interestRate: {
    id: 'EstimatedMonthlyPaymentBreakdown.interestRate',
    defaultMessage: 'Interest rate'
  },
  loanProgram30Fixed: {
    id: 'EstimatedMonthlyPaymentBreakdown.loanProgram30Fixed',
    defaultMessage: '30 year fixed'
  },
  loanProgram15Fixed: {
    id: 'EstimatedMonthlyPaymentBreakdown.loanProgram15Fixed',
    defaultMessage: '15 year fixed'
  },
  show: {
    id: 'EstimatedMonthlyPaymentBreakdown.show',
    defaultMessage: 'Show editable fields'
  },
  hide: {
    id: 'EstimatedMonthlyPaymentBreakdown.hide',
    defaultMessage: 'Hide editable fields'
  }
})

export interface EstimatedMonthlyPaymentBreakdownPrincipalAndInterestProps {
  rate: string
  loanTerm: number
  homePrice: string
  creditScore: number
  downPaymentCash: string
  assumableToggle?: boolean
  isCalculatorsPage?: boolean
  principalAndInterest: string
  downPaymentPercentage: string
  setRate: (rate: string) => void
  setLoanTerm: (loanTerm: number) => void
  secondMortgagePrincipalAndInterest: string
  setCreditScore: (creditScore: number) => void
  assumableMortgageLoanInfo?: ListingLoanInfo | null
  setDownPaymentCash: (downPaymentCash: string) => void
  assumableMortgageSecondLoanInfo?: ListingLoanInfo | null
  setDownPaymentPercentage: (downPaymentPercentage: string) => void
  assumableBucketedMonthlyHomeExpenses?: BucketedMonthlyHomeExpenses | null
  setHasUsedMortgageCalculator: (hasUsedMortgageCalculator: boolean) => void
}

export const EstimatedMonthlyPaymentBreakdownPrincipalAndInterest = ({
  rate,
  setRate,
  loanTerm,
  homePrice,
  setLoanTerm,
  creditScore,
  setCreditScore,
  downPaymentCash,
  isCalculatorsPage,
  setDownPaymentCash,
  principalAndInterest,
  downPaymentPercentage,
  setDownPaymentPercentage,
  assumableMortgageLoanInfo,
  setHasUsedMortgageCalculator,
  secondMortgagePrincipalAndInterest,
  assumableBucketedMonthlyHomeExpenses,
  assumableToggle = false
}: EstimatedMonthlyPaymentBreakdownPrincipalAndInterestProps) => {
  const intl = useIntl()
  const { theme } = useTheme()
  const isUserTypingDownPaymentCash = useRef(false)
  const isUserTypingDownPaymentPercentage = useRef(false)
  const [showPaymentHint, setShowPaymentHint] = useState(false)
  const [showEditableFields, setShowEditableFields] = useState(true)
  const [isEditingDownPaymentCash, setIsEditingDownPaymentCash] = useState(false)
  const [isEditingDownPaymentPercentage, setIsEditingDownPaymentPercentage] = useState(false)

  const handleChangeDownPaymentCash = (cash: string) => {
    setIsEditingDownPaymentCash(true)

    // Let's not exceed homePrice
    if (
      assumableToggle
        ? Number(cash) + Number(assumableMortgageLoanInfo?.loanAmount) > Number(homePrice)
        : Number(cash) > Number(homePrice)
    ) {
      setShowPaymentHint(true)
      return
    }

    setDownPaymentCash(cash)
    setDownPaymentPercentage(
      assumableToggle
        ? ((Number(cash) / (Number(homePrice) - Number(assumableMortgageLoanInfo?.loanAmount))) * 100).toFixed(2)
        : ((Number(cash) / Number(homePrice)) * 100).toFixed(2)
    )

    if (isUserTypingDownPaymentCash.current && !assumableToggle) {
      localStorage?.removeItem('downPaymentPercentage')
      localStorage?.setItem('downPaymentCash', cash || '0')
    }

    setTimeout(() => {
      setIsEditingDownPaymentCash(false)
    }, 1000)
  }

  const handleChangeDownPaymentPercentage = (percentage: number) => {
    setIsEditingDownPaymentPercentage(true)

    const percentageString = percentage.toString()

    let percentageNumber = Number(parseFloat(percentageString)) || 0
    const decimalPointIndex = percentageString.indexOf('.')

    // Let's not exceed 100%
    if (percentageNumber > 100) {
      setShowPaymentHint(true)
      return
    }

    // Let's not have more than 3 decimal places
    if (decimalPointIndex !== -1 && percentageString.length - decimalPointIndex - 1 > 3) {
      return
    }

    setDownPaymentPercentage(percentageNumber.toString())
    setDownPaymentCash(
      assumableToggle
        ? (((Number(homePrice) - Number(assumableMortgageLoanInfo?.loanAmount)) * percentageNumber) / 100).toFixed(2)
        : ((Number(homePrice) * percentageNumber) / 100).toFixed(2)
    )

    if (isUserTypingDownPaymentPercentage.current && !assumableToggle) {
      localStorage?.removeItem('downPaymentCash')
      localStorage?.setItem('downPaymentPercentage', percentage.toString())
    }

    setTimeout(() => {
      setIsEditingDownPaymentPercentage(false)
    }, 1000)
  }

  const handleChangeCreditScore = (creditScore: number) => {
    setCreditScore(creditScore)
    localStorage?.setItem('creditScore', creditScore.toString())
  }

  const showSecondMortgage = Boolean(
    assumableToggle && secondMortgagePrincipalAndInterest && Number(secondMortgagePrincipalAndInterest) > 0
  )

  return (
    <Card p={3}>
      <Button
        w='100%'
        variant='link'
        display='block'
        textAlign='left'
        fontWeight='inherit'
        textColor='currentColor'
        _hover={{ textDecoration: 'none' }}
        onClick={() => setShowEditableFields(!showEditableFields)}
        tracking={{
          guid: 'qui6FqNxVwnJnvjgkC9Yp8',
          ui_context: isCalculatorsPage
            ? 'Mortgage Calculator Page'
            : HOME_SEARCH_LISTING_DETAILS_ESTIMATED_MONTHLY_PAYMENT,
          descriptor: `Principal & Interest ${showEditableFields ? 'Hide' : 'Show'}`
        }}
        aria-label={intl.formatMessage(showEditableFields ? MSG.hide : MSG.show)}
      >
        <Box wordBreak={'break-word'} whiteSpace={'normal'} overflow={'hidden'}>
          {assumableToggle &&
            (showSecondMortgage ? (
              <Text size={'sm'}>
                {intl.formatMessage(MSG.assumableExplainerWithSecondMortgage, {
                  down: (
                    <strong>
                      {intl.formatMessage(MSG.downPaymentInline, { down: formatCurrency(downPaymentCash || 0) })}
                    </strong>
                  )
                })}
              </Text>
            ) : (
              <Text>
                {intl.formatMessage(MSG.assumableExplainerNoSecondMortgage, {
                  down: (
                    <strong>
                      {intl.formatMessage(MSG.downPaymentInline, { down: formatCurrency(downPaymentCash || 0) })}
                    </strong>
                  )
                })}
              </Text>
            ))}
        </Box>
        <Flex mb={2} align='center' justify='space-between'>
          <Flex gap={2} align='center'>
            <Box bg='success.700' width={3} height={3} borderRadius='50%' />
            <Heading as='h4' size={['2xs', 'xs', 'sm']}>
              {assumableToggle ? intl.formatMessage(MSG.assumedMortgage) : intl.formatMessage(MSG.principalAndInterest)}
            </Heading>
          </Flex>
          <Flex align='center' gap={1}>
            <Text
              mb={0}
              color='neutral.900'
              size={['xs', 'sm', 'md']}
              fontWeight='semibold'
              style={{ fontVariant: 'tabular-nums' }}
            >
              {assumableToggle
                ? formatCurrency(assumableBucketedMonthlyHomeExpenses?.monthlyPrincipalAndInterest)
                : formatCurrency(principalAndInterest)}
            </Text>
            <Icon
              height={2}
              name='play'
              transform={`rotate(${showEditableFields ? -90 : 90}deg)`}
              color='neutral.900'
            />
          </Flex>
        </Flex>

        <Flex gap={0} align='baseline' justifyContent='flex-start' direction={['column', 'row']}>
          {assumableToggle ? (
            <Box ml={5} />
          ) : (
            <>
              <Text ml={5} as='span' size={['2xs', '2xs', 'sm']}>
                <Text as='span' size={['2xs', '2xs', 'sm']} color='success.700' fontWeight='bold'>
                  {intl.formatMessage(MSG.down, {
                    down: formatCurrency(downPaymentCash || 0)
                  })}
                </Text>
              </Text>
              <Text as='span' mx={'1ch'} size={['2xs', '2xs', 'sm']} fontWeight='bold' display={['none', 'initial']}>
                •
              </Text>
            </>
          )}
          <Text ml={[5, 0]} as='span' size={['2xs', '2xs', 'sm']}>
            {assumableToggle
              ? intl.formatMessage(MSG.assumedMortgageTermAndRate, {
                  years: assumableMortgageLoanInfo?.termYears,
                  rate: assumableMortgageLoanInfo?.interestRatePercent
                    ? parseFloat(assumableMortgageLoanInfo?.interestRatePercent.toFixed(3))
                    : ''
                })
              : intl.formatMessage(MSG.principalAndInterestTermAndRate, {
                  years: loanTerm,
                  rate: rate
                })}
          </Text>
        </Flex>

        {/* Second Mortgage, optional */}

        {showSecondMortgage && (
          <>
            <Flex marginY={2} align='center' justify='space-between'>
              <Flex gap={2} align='center'>
                <Box
                  width={3}
                  height={3}
                  borderRadius='50%'
                  bg={theme.isLightThemeType ? 'success.400' : 'success.900'}
                />
                <Heading as='h4' size={['2xs', 'xs', 'sm']}>
                  {intl.formatMessage(MSG.secondMortgage)}
                </Heading>
              </Flex>
              <Text
                mb={0}
                mr={6}
                color='neutral.900'
                size={['xs', 'sm', 'md']}
                fontWeight='semibold'
                style={{ fontVariant: 'tabular-nums' }}
              >
                {formatCurrency(Number(secondMortgagePrincipalAndInterest))}
              </Text>
            </Flex>

            <Flex align='baseline' justifyContent='flex-start' direction={['column', 'row']} gap={0}>
              <Text ml={5} as='span' size={['2xs', '2xs', 'sm']}>
                {intl.formatMessage(MSG.secondMortgageTermAndRate, {
                  years: loanTerm,
                  rate: rate
                })}
              </Text>
            </Flex>
          </>
        )}
      </Button>

      {showEditableFields && (
        <Flex mt={4} gap={4} direction='column' alignItems='flex-start'>
          <Flex width='100%' direction='column' gap={2} alignItems='flex-start'>
            <Flex gap={2} width='100%'>
              <FormControl>
                <FormLabel size='xs'>{intl.formatMessage(MSG.downPayment)}</FormLabel>
                <CurrencyInput
                  min={0}
                  size='sm'
                  decimalScale={2}
                  name='downPaymentCash'
                  max={Number(homePrice)}
                  value={downPaymentCash}
                  onFocus={() => {
                    setHasUsedMortgageCalculator(true)
                    isUserTypingDownPaymentCash.current = true
                  }}
                  onBlur={() => (isUserTypingDownPaymentCash.current = false)}
                  // @ts-ignore
                  onChange={val => !isEditingDownPaymentPercentage && handleChangeDownPaymentCash(val)}
                  tracking={{
                    guid: 'rFTRcPY1nfPRTj4m6SnUhy',
                    ui_context: isCalculatorsPage
                      ? 'Mortgage Calculator Page'
                      : HOME_SEARCH_LISTING_DETAILS_ESTIMATED_MONTHLY_PAYMENT,
                    descriptor: 'Down payment cash input field'
                  }}
                />
                {showPaymentHint && (
                  <FormHelperText fontSize='3xs'>
                    $
                    {assumableToggle
                      ? formatNumber(Number(homePrice) - Number(assumableMortgageLoanInfo?.loanAmount))
                      : formatNumber(Number(homePrice))}{' '}
                    {intl.formatMessage(MSG.max)}
                  </FormHelperText>
                )}
              </FormControl>
              <Box mt={7} height={8} alignContent='center' justifyContent='center'>
                <Text as='span'>{intl.formatMessage(MSG.or)}</Text>
              </Box>
              <FormControl>
                <FormLabel size='xs'>{intl.formatMessage(MSG.percentage)}</FormLabel>
                <InputGroup size='sm'>
                  <Input
                    pr={5}
                    min={0}
                    max={100}
                    type='number'
                    inputMode='decimal'
                    name='downPaymentPercentage'
                    transition='color 0s ease-in-out'
                    value={downPaymentPercentage || ''}
                    onFocus={() => {
                      setHasUsedMortgageCalculator(true)
                      isUserTypingDownPaymentPercentage.current = true
                    }}
                    onBlur={() => (isUserTypingDownPaymentPercentage.current = false)}
                    onChange={e => {
                      if (isEditingDownPaymentCash) return

                      let inputValue = e.target.value
                      if (inputValue.startsWith('0') && inputValue.length > 4) {
                        inputValue = inputValue.replace(/^0+/, '')
                      }
                      const numericValue = parseFloat(inputValue)
                      if (!isNaN(numericValue)) {
                        handleChangeDownPaymentPercentage(numericValue)
                      } else if (inputValue === '0.') {
                        handleChangeDownPaymentPercentage(numericValue)
                      } else if (inputValue === '') {
                        handleChangeDownPaymentPercentage(0)
                      }
                    }}
                    tracking={{
                      guid: '1DiJwBHoPY1mgks79RpnmN',
                      ui_context: isCalculatorsPage
                        ? 'Mortgage Calculator Page'
                        : HOME_SEARCH_LISTING_DETAILS_ESTIMATED_MONTHLY_PAYMENT,
                      descriptor: 'Down payment percentage input field'
                    }}
                  />
                  <InputRightElement pointerEvents='none' fontSize='xs' mr={-0.5}>
                    %
                  </InputRightElement>
                </InputGroup>
                {showPaymentHint && (
                  <FormHelperText fontSize='3xs' textAlign='right'>
                    100% {intl.formatMessage(MSG.max)}
                  </FormHelperText>
                )}
              </FormControl>
            </Flex>
          </Flex>
          <FormControl>
            <FormLabel size='xs'>{intl.formatMessage(MSG.loanProgram)}</FormLabel>
            <Select
              size='sm'
              value={loanTerm}
              name='loanProgram'
              onFocus={() => setHasUsedMortgageCalculator(true)}
              onChange={e => setLoanTerm(Number(e.target.value))}
              tracking={{
                guid: '2yF53B95YnqbXWW3Npo7Lk',
                ui_context: isCalculatorsPage
                  ? 'Mortgage Calculator Page'
                  : HOME_SEARCH_LISTING_DETAILS_ESTIMATED_MONTHLY_PAYMENT,
                descriptor: 'Loan program select'
              }}
            >
              <option value={30}>{intl.formatMessage(MSG.loanProgram30Fixed)}</option>
              <option value={15}>{intl.formatMessage(MSG.loanProgram15Fixed)}</option>
            </Select>
          </FormControl>
          <Flex mb={2} gap={2} width='100%'>
            <FormControl>
              <FormLabel size='xs'>{intl.formatMessage(MSG.creditScore)}</FormLabel>
              <Select
                size='sm'
                name='creditScore'
                value={creditScore}
                onFocus={() => setHasUsedMortgageCalculator(true)}
                onChange={e => handleChangeCreditScore(Number(e.target.value))}
                tracking={{
                  guid: 'sMvqHjKocpGnzgBVJ456Ec',
                  ui_context: isCalculatorsPage
                    ? 'Mortgage Calculator Page'
                    : HOME_SEARCH_LISTING_DETAILS_ESTIMATED_MONTHLY_PAYMENT,
                  descriptor: 'Credit score select'
                }}
              >
                {Object.keys(CREDIT_SCORE_VALUE_TO_RANGES).map(key => (
                  <option key={key} value={key}>
                    {CREDIT_SCORE_VALUE_TO_RANGES[key]}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel size='xs'>{intl.formatMessage(MSG.interestRate)}</FormLabel>
              <InputGroup size='sm'>
                <Input
                  size='sm'
                  type='number'
                  value={rate || ''}
                  name='interestRate'
                  onChange={e => setRate(e.target.value)}
                  onFocus={() => setHasUsedMortgageCalculator(true)}
                  tracking={{
                    guid: 'v6TazD5Nn7rYFCuLV4SBRJ',
                    ui_context: isCalculatorsPage
                      ? 'Mortgage Calculator Page'
                      : HOME_SEARCH_LISTING_DETAILS_ESTIMATED_MONTHLY_PAYMENT,
                    descriptor: 'Interest rate input field'
                  }}
                />
                <InputRightElement pointerEvents='none' fontSize='xs' mr={-0.5}>
                  %
                </InputRightElement>
              </InputGroup>
            </FormControl>
          </Flex>
        </Flex>
      )}
    </Card>
  )
}
